import { useTrackToggle } from "@livekit/components-react";
import { Track } from "livekit-client";

type PanelKind = "none" | "chat" | "whiteboard" | "participants" | "poll";

interface ControlBarProps {
  isHost: boolean;
  myRaised: boolean;
  raisedCount: number;
  onToggleRaise: () => void;
  activePanel: PanelKind;
  onTogglePanel: (panel: "chat" | "whiteboard" | "participants" | "poll") => void;
  unreadChat: number;
  participantCount: number;
  micAllowed: boolean;
  camAllowed: boolean;
  elapsedSeconds: number;
}

interface ControlButtonProps {
  label: string;
  active: boolean;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function ControlButton({ label, active, danger, disabled, onClick, children }: ControlButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="group flex flex-col items-center gap-1 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
      aria-label={label}
      title={label}
    >
      <span
        className={`relative w-11 h-11 flex items-center justify-center rounded-full text-xl transition-colors ${
          disabled
            ? "bg-background-800 text-background-600"
            : active
            ? danger
              ? "bg-red-600 text-white"
              : "bg-primary-500 text-white"
            : "bg-background-700 text-background-200 hover:bg-background-600"
        }`}
      >
        {children}
      </span>
      <span className="text-[10px] text-background-400 group-disabled:text-background-600">{label}</span>
    </button>
  );
}

function formatTime(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function ControlBar({
  isHost,
  myRaised,
  raisedCount,
  onToggleRaise,
  activePanel,
  onTogglePanel,
  unreadChat,
  participantCount,
  micAllowed,
  camAllowed,
  elapsedSeconds,
}: ControlBarProps) {
  const { enabled: micEnabled, toggle: toggleMic } = useTrackToggle({
    source: Track.Source.Microphone,
  });
  const { enabled: camEnabled, toggle: toggleCam } = useTrackToggle({
    source: Track.Source.Camera,
  });
  const { enabled: screenEnabled, toggle: toggleScreen } = useTrackToggle({
    source: Track.Source.ScreenShare,
  });

  const micDisabled = !isHost && !micAllowed;
  const camDisabled = !isHost && !camAllowed;

  return (
    <div className="shrink-0 bg-background-900 border-t border-background-700/50">
      {/* Locked notice for students */}
      {!isHost && (micDisabled || camDisabled) && (
        <div className="px-4 py-1.5 flex items-center gap-2 bg-background-800 border-b border-background-700/30">
          <i className="ri-lock-line text-xs text-background-500" aria-hidden="true"></i>
          <p className="text-[11px] text-background-500">
            {micDisabled && camDisabled
              ? "Mikrofon ve kamera öğretmen tarafından kısıtlandı."
              : micDisabled
              ? "Mikrofon öğretmen tarafından kısıtlandı."
              : "Kamera öğretmen tarafından kısıtlandı."}
          </p>
        </div>
      )}

      <div className="px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
        {/* Left: timer */}
        <div className="flex items-center gap-1.5 min-w-[60px]">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" aria-hidden="true"></span>
          <span className="text-xs font-mono font-semibold text-red-400">{formatTime(elapsedSeconds)}</span>
        </div>

        {/* Center: controls */}
        <div className="flex items-center justify-center gap-3 md:gap-5 flex-wrap flex-1">
          <ControlButton
            label={micEnabled ? "Mikrofon" : "Mikrofon Kapalı"}
            active={micEnabled}
            disabled={micDisabled}
            onClick={toggleMic}
          >
            <i className={micEnabled ? "ri-mic-line" : "ri-mic-off-line"} aria-hidden="true"></i>
          </ControlButton>

          <ControlButton
            label={camEnabled ? "Kamera" : "Kamera Kapalı"}
            active={camEnabled}
            disabled={camDisabled}
            onClick={toggleCam}
          >
            <i className={camEnabled ? "ri-vidicon-line" : "ri-vidicon-line"} aria-hidden="true"></i>
          </ControlButton>

          <ControlButton label="Ekran Paylaş" active={screenEnabled} onClick={toggleScreen}>
            <i className="ri-computer-line" aria-hidden="true"></i>
          </ControlButton>

          {!isHost && (
            <ControlButton
              label={myRaised ? "Elini İndir" : "El Kaldır"}
              active={myRaised}
              onClick={onToggleRaise}
            >
              <span className="relative">
                <i className="fa-solid fa-hand" aria-hidden="true"></i>
                {raisedCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-accent-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {raisedCount}
                  </span>
                )}
              </span>
            </ControlButton>
          )}

          <ControlButton
            label="Sohbet"
            active={activePanel === "chat"}
            onClick={() => onTogglePanel("chat")}
          >
            <span className="relative">
              <i className="ri-chat-3-line" aria-hidden="true"></i>
              {unreadChat > 0 && activePanel !== "chat" && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {unreadChat}
                </span>
              )}
            </span>
          </ControlButton>

          <ControlButton
            label="Tahta"
            active={activePanel === "whiteboard"}
            onClick={() => onTogglePanel("whiteboard")}
          >
            <i className="ri-mark-pen-line" aria-hidden="true"></i>
          </ControlButton>

          <ControlButton
            label="Anket"
            active={activePanel === "poll"}
            onClick={() => onTogglePanel("poll")}
          >
            <i className="ri-bar-chart-2-line" aria-hidden="true"></i>
          </ControlButton>

          {isHost && (
            <ControlButton
              label={`Sınıf (${participantCount})`}
              active={activePanel === "participants"}
              onClick={() => onTogglePanel("participants")}
            >
              <span className="relative">
                <i className="ri-group-line" aria-hidden="true"></i>
                {raisedCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-accent-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                    {raisedCount}
                  </span>
                )}
              </span>
            </ControlButton>
          )}
        </div>

        {/* Right: spacer */}
        <div className="min-w-[60px]" />
      </div>
    </div>
  );
}